/**
 * RI EMS PCR Narrative — Call Data Schema
 *
 * Structured slot model defined by Protocol 1.02 ¶6 data points.
 * Every required slot has a Protocol 1.02 paragraph reference. The
 * generator either fills the slot from `call_data` or emits an
 * explicit "denied / none / not obtainable" sentence per Section 4
 * of the design doc.
 */

/**
 * Provider scope tier — RI Statewide EMS Protocols 2026.01 §1.01.
 * Each level can perform everything below it plus its own scope
 * additions. Per the design-doc Change 1, every intervention,
 * medication, and procedure carries a `min_scope_level` flag and the
 * generator filters at slot-selection time.
 */
export type ProviderLevel = 'EMT' | 'AEMT' | 'AEMT-Cardiac' | 'Paramedic';

export const PROVIDER_LEVEL_RANK: Record<ProviderLevel, number> = {
  EMT: 1,
  AEMT: 2,
  'AEMT-Cardiac': 3,
  Paramedic: 4,
};

export function scopeRank(level: ProviderLevel | null | undefined): number {
  return level ? PROVIDER_LEVEL_RANK[level] : 0;
}

export function withinScope(userLevel: ProviderLevel, requiredLevel: ProviderLevel | null | undefined): boolean {
  if (!requiredLevel) return true;
  return scopeRank(userLevel) >= scopeRank(requiredLevel);
}

/**
 * Drug → minimum provider level. Pulled from the RI 2026.01
 * formulary scope-of-practice matrix. Anything not listed defaults
 * to AEMT (IV-access tier) since the bare-bones EMT formulary in RI
 * is small and well-defined.
 */
export const MIN_SCOPE_BY_DRUG: Record<string, ProviderLevel> = {
  // EMT scope (BLS, RI 2026.01 §1.01 + Protocol 7.10 finger-stick BG)
  ASPIRIN:                   'EMT',
  OXYGEN:                    'EMT',
  GLUCOSE:                   'EMT',
  'ORAL GLUCOSE':            'EMT',
  ALBUTEROL:                 'EMT',     // patient's own inhaler assist + EMT-level SVN
  EPINEPHRINE:               'EMT',     // auto-injector at EMT level (anaphylaxis)
  NALOXONE:                  'EMT',     // intranasal at EMT level
  NARCAN:                    'EMT',
  // AEMT scope (IV access + select meds)
  'NORMAL SALINE':           'AEMT',
  'LACTATED RINGERS':        'AEMT',
  'LACTATED RINGER\'S':      'AEMT',
  D5W:                       'AEMT',
  D10:                       'AEMT',
  D50:                       'AEMT',
  DEXTROSE:                  'AEMT',
  ZOFRAN:                    'AEMT',
  ONDANSETRON:               'AEMT',
  GLUCAGON:                  'AEMT',
  IPRATROPIUM:               'AEMT',
  // AEMT-Cardiac scope (RI endorsement: 12-lead, NTG, etc.)
  NITROGLYCERIN:             'AEMT-Cardiac',
  // Paramedic scope (controlled / advanced)
  FENTANYL:                  'Paramedic',
  KETAMINE:                  'Paramedic',
  MIDAZOLAM:                 'Paramedic',
  MORPHINE:                  'Paramedic',
  LORAZEPAM:                 'Paramedic',
  DIAZEPAM:                  'Paramedic',
  AMIODARONE:                'Paramedic',
  ATROPINE:                  'Paramedic',
  ROCURONIUM:                'Paramedic',
  ETOMIDATE:                 'Paramedic',
  METHYLPREDNISOLONE:        'Paramedic',
  DEXAMETHASONE:             'Paramedic',
  ADENOSINE:                 'Paramedic',
  DILTIAZEM:                 'Paramedic',
  HALOPERIDOL:               'Paramedic',
  KETOROLAC:                 'Paramedic',
  TRANEXAMIC:                'Paramedic',
  TXA:                       'Paramedic',
  HYDROCORTISONE:            'Paramedic',
  PREDNISONE:                'Paramedic',
  MAGNESIUM:                 'Paramedic',
  'CALCIUM CHLORIDE':        'Paramedic',
  'CALCIUM GLUCONATE':       'Paramedic',
  'SODIUM BICARBONATE':      'Paramedic',
  PHENYLEPHRINE:             'Paramedic',
  NOREPINEPHRINE:            'Paramedic',
  TENECTEPLASE:              'Paramedic',
  TNK:                       'Paramedic',
  'NITROUS OXIDE':           'Paramedic',
  NITROUS:                   'Paramedic',
};

/**
 * Procedure → minimum provider level. Same matrix as drugs but for
 * skills (12-lead, IV, intubation, etc.).
 */
export const MIN_SCOPE_BY_PROCEDURE: Record<string, ProviderLevel> = {
  // EMT scope
  'BLOOD GLUCOSE':           'EMT',     // Protocol 7.10 — RI EMT-level BG
  'PULSE OXIMETRY':          'EMT',
  'BVM':                     'EMT',
  'CPR':                     'EMT',
  'AED':                     'EMT',
  // AEMT scope
  'PERIPHERAL IV':           'AEMT',
  'INTRAVENOUS ACCESS':      'AEMT',
  'IV':                      'AEMT',
  // AEMT-Cardiac scope (RI endorsement — Protocol 7.18)
  '12-LEAD ECG':             'AEMT-Cardiac',
  '12 LEAD ECG':             'AEMT-Cardiac',
  'TWELVE-LEAD ECG':         'AEMT-Cardiac',
  ECG:                       'AEMT-Cardiac',
  // Paramedic scope
  'ENDOTRACHEAL INTUBATION': 'Paramedic',
  'OETI':                    'Paramedic',
  'NASOTRACHEAL':            'Paramedic',
  CRICOTHYROTOMY:            'Paramedic',
  DEFIBRILLATION:            'Paramedic',     // manual; AED is EMT
  'MANUAL DEFIBRILLATION':   'Paramedic',
  CARDIOVERSION:             'Paramedic',
  PACING:                    'Paramedic',
  'NEEDLE THORACOSTOMY':     'Paramedic',
};

export function minScopeForDrug(drug: string): ProviderLevel {
  const key = drug.toUpperCase();
  return MIN_SCOPE_BY_DRUG[key] ?? 'AEMT';   // default AEMT if unknown
}

export function minScopeForProcedure(name: string): ProviderLevel {
  const key = name.toUpperCase();
  if (MIN_SCOPE_BY_PROCEDURE[key]) return MIN_SCOPE_BY_PROCEDURE[key];
  // Fuzzy: pattern-match against procedure-name fragments.
  if (/12[-\s]?lead|ECG/i.test(name)) return 'AEMT-Cardiac';
  if (/intubation|cricothyrot|defibrillat|cardiovers|pacing|thoracostomy/i.test(name)) return 'Paramedic';
  if (/IV\b|intravenous|IO\b|intraosseous/i.test(name)) return 'AEMT';
  if (/BVM|AED|oxim|glucose|pulse\s*ox/i.test(name)) return 'EMT';
  return 'AEMT';
}

export interface VitalsSet {
  time: string;       // 24-hour, "1444"
  bp: string;         // "152/94"
  pulse: string;      // "88"
  rr: string;         // "18"
  spo2: string;       // "96"
  o2: string;         // "room air" | "2 lpm nasal cannula" | "non-rebreather mask"
  /** Optional capture-point label so the parser can group these
   *  with the intervention they're paired with. ImageTrend regex
   *  parsing uses the standardized "Vitals at HHMM" anchor. */
  context?: 'initial' | 'post-medication' | 'pre-transport' | 'pre-handoff' | 'post-status-change' | 'reassessment';
}

export interface MedicationAdmin {
  drug: string;
  dose: string;        // "324 mg"
  route: string;       // "PO" | "IV" | "IM" | "SL" | "IN" | etc.
  time: string;        // "1446"
  performer: string;   // "paramedic" | "AEMT" | "EMT"
  response: string;    // "Patient chewed and swallowed without difficulty."
  /** Minimum provider scope to administer this drug. Auto-derived
   *  from MIN_SCOPE_BY_DRUG when unset. Used by the assembler to
   *  filter interventions[] against the call's provider_level. */
  min_scope_level?: ProviderLevel;
  /** True for assisted self-administration with patient's own
   *  supply (own inhaler, own NTG, own EpiPen). Section 7
   *  prevents bold formatting and inserts the assist clause. */
  patientOwn?: boolean;
  /** True for oxygen / IV fluids / continuous infusions. Section 7
   *  prevents bold formatting (these aren't restock-relevant). */
  excludeFromBold?: boolean;
  /** True for fentanyl / ketamine / midazolam / morphine /
   *  lorazepam / diazepam / N2O. Appends "[CS]". */
  controlledSubstance?: boolean;
  /** Per design-doc Change 2: when true, the operator is prompted
   *  to capture vitals at this timestamp, the values render inline
   *  in the narrative, and the EHR parser pulls them into the
   *  structured vitals tab. Default true for medication
   *  administrations and procedures per Protocol 1.02 ¶9 ("Repeat
   *  after drug administration, before transfer, and as needed"). */
  auto_capture_vitals?: boolean;
}

export interface ProcedureRecord {
  procedure: string;          // "An 18-gauge IV"
  location: string;           // "right antecubital"
  attempt: string;            // "first" | "second" | …
  outcome: string;            // "without complication" | "with one missed attempt before success"
  performer: string;
  time: string;
  complications?: string;
  /** Auto-derived from MIN_SCOPE_BY_PROCEDURE when unset. */
  min_scope_level?: ProviderLevel;
  /** Trigger a vitals capture prompt at this timestamp. Default
   *  true for 12-lead ECG, defibrillation, cardioversion. */
  auto_capture_vitals?: boolean;
}

export interface CallData {
  // §1.01 — provider level driving the scope filter. The
  // assembler hides interventions whose min_scope_level exceeds
  // this. EMT runs of a chest-pain call don't see NTG or fentanyl
  // prompts; AEMT-Cardiac sees NTG but not fentanyl; Paramedic
  // sees everything.
  provider_level: ProviderLevel;
  // §1.02 ¶6 data point 1 — chief complaint (required)
  chief_complaint: string;
  // §1.02 ¶6 data point 2 — MOI (required if applicable)
  moi: string | null;
  // §1.02 ¶6 data point 3 — source if not patient
  history_source: string | null;
  // §1.02 ¶6 data point 4 — HPI (required)
  hpi: string;
  // §1.02 ¶6 data point 5 — PMH (required; "none reported" if absent)
  pmh: string | null;
  // §1.02 ¶6 data point 6 — family/social (required only if relevant)
  family_social_hx: string | null;
  // §1.02 ¶6 data point 7 — meds (required; "none" if absent)
  medications: string | null;
  // §1.02 ¶6 data point 8 — allergies (required; "NKDA" if absent)
  allergies: string | null;
  // §1.02 ¶6 data point 9 — exam findings (required)
  exam_findings: string;
  // §1.02 ¶6 data point 10 — ECG / POC labs
  ecg_interpretation: string | null;
  bg_value: string | null;
  // §1.02 ¶6 data point 11 — field diagnosis (required)
  field_diagnosis: string;
  // §1.02 ¶6 data point 12 + ¶7/¶8 — interventions
  interventions: MedicationAdmin[];
  procedures: ProcedureRecord[];
  // §1.02 ¶9 — minimum two complete sets for transports, one for
  // non-transport. The legacy two-slot model is preserved for
  // backwards compatibility; the design-doc Change 2 replaces it
  // with the `vitals` array below for inline-capture rendering.
  vitals_set_1: VitalsSet | null;
  vitals_set_2: VitalsSet | null;
  /** Inline-capture vitals (Change 2). Rendered in standardized
   *  format ("Vitals at HHMM: BP X/Y, pulse Z, respirations W,
   *  SpO2 V percent on Q") interleaved with intervention
   *  sentences. ImageTrend regex parsing pulls these into the
   *  structured vitals tab. */
  vitals: VitalsSet[];
  // §1.02 ¶11 — receiving provider (required)
  receiving_provider_name: string | null;
  receiving_provider_credential: 'RN' | 'APRN' | 'PA' | 'MD' | 'DO' | null;
  handoff_time: string | null;
  // Dispatch context (D in DCHARTE / DISPATCH)
  dispatch_location: string | null;
  dispatch_reason: string | null;
  arrival_finding: string | null;
  patient_line: string | null;
  // Transport
  destination: string | null;
  alert_activation: string | null;     // "STEMI alert" | "stroke alert" | etc.
  is_transport: boolean;
  // Section flags
  is_interfacility: boolean;
  scene_complexity: boolean;            // MVC + extrication, scene-safety, peds welfare
  status_change: boolean;               // status changed during transport
  exceptions: string | null;            // DCHARTE E content
  protocols_invoked: string[];          // protocol names list ("3.02 STEMI", "1.01 Routine Patient Care")
  // SBAR specifics
  reason_for_transport: string | null;
  current_status: string | null;
  specific_ask: string | null;
}

export const REQUIRED_DATA_POINTS = [
  'chief_complaint',  // ¶6.1
  'hpi',              // ¶6.4
  'pmh',              // ¶6.5  (or "none reported")
  'medications',      // ¶6.7  (or "none")
  'allergies',        // ¶6.8  (or "NKDA")
  'exam_findings',    // ¶6.9
  'field_diagnosis',  // ¶6.11
] as const satisfies readonly (keyof CallData)[];

/**
 * Controlled substances under RI law per Protocol 1.02 design doc §7.
 * Used by the assembler to auto-stamp [CS] on matching meds.
 */
export const RI_CONTROLLED_SUBSTANCES = new Set([
  'FENTANYL', 'KETAMINE', 'MIDAZOLAM', 'MORPHINE',
  'LORAZEPAM', 'DIAZEPAM', 'NITROUS OXIDE', 'NITROUS',
]);

/**
 * Drug classes excluded from bold formatting per §7. These are not
 * restock-relevant — fluids, oxygen — or are patient-owned.
 */
export const NON_RESTOCK_BOLD_EXCLUSIONS = new Set([
  'OXYGEN', 'NORMAL SALINE', 'LACTATED RINGERS', 'LACTATED RINGER\'S',
  'D5W', 'D5LR', 'SALINE',
]);
